import { useIonAlert } from '@ionic/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Subscription } from 'rxjs';

import { getRepairOrderByRepairOrderNumber } from '../api/dms/repairOrder.api';
import { ITimerItem, startTimer } from '../common/interval';
import { logConsole, logTransaction } from '../common/log';
import { getStateStringAsMoment } from '../common/util';
import { AttachmentAction, AttachmentAssetType, IAttachment, IAttachmentContainer, IUpdateMediaAttachments } from '../models/dms/attachment.model';
import { IBranch, IBranchSyncStatus } from '../models/dms/branch.model';
import { ClockingDirection, ClockingType, IAttendanceClocking, ILabourClocking, ITechClockingDetail, ITechClockingLabour } from '../models/dms/clocking.model';
import { IRepairOrder, IRepairOrderSyncStatus, mapRepairOrderDetailToRepairOrderWithJobs } from '../models/dms/repairOrder.model';
import { IRepairOrderJob } from '../models/dms/repairOrderJob.model';
import { IClockingRequestQueueItem, ITechCommentRequestQueueItem, IVehicleAttachmentRequestQueueItem, RequestQueueType } from '../models/dms/requestQueue.model';
import { IWorkCategory, IWorkCategorySyncStatus } from '../models/dms/technician.model';
import { ITechComment } from '../models/dms/technicianComments.model';
import { IDelayCodeSyncStatus, ITypeCode } from '../models/dms/typecode.model';
import { IThunkError } from '../models/error.model';
import { LogType } from '../models/log.model';
import { EmptyOdataResult } from '../models/odata.model';
import store from '../store';
import { setAllowFetchClockingsState, setAllowOverrideAttendanceState, setAllowOverrideLabourState, setIsInActionState, subtractClockingFetchCooldownState } from '../store/appSlice';
import { fetchBranchesStoreAsync, setBranchesHasSyncStoreAsync, setBranchesSyncStoreAsync } from '../store/branchSyncSlice';
import { fetchDelayCodesStoreAsync, setDelayCodesHasSyncStoreAsync, setDelayCodesSyncStoreAsync } from '../store/delayCodeSyncSlice';
import { useAppDispatch } from '../store/hooks';
import { setSelectedRepairOrderStoreAsync } from '../store/repairOrderSlice';
import { fetchRepairOrdersStoreAsync, setRepairOrderHasSyncStoreAsync, setRepairOrderSyncStoreAsync } from '../store/repairOrderSyncSlice';
import { removeFromRequestQueueStoreAsync, updateRequestQueueStoreAsync } from '../store/requestQueueSlice';
import { fetchClockingDetailsAsync, postClockOffAttendanceAsync, postClockOffLabourAsync, postClockOnAttendanceAsync, postClockOnLabourAsync, setAttendanceClockingStoreAsync, setLabourClockingStoreAsync } from '../store/technicianClockingSlice';
import { postTechnicianCommentAsync } from '../store/technicianCommentSlice';
import { postVehicleAttachmentAsync, setAttachmentsProcessingState } from '../store/vehicleAttachmentSlice';
import { fetchWorkCategoriesStoreAsync, setWorkCategoriesHasSyncStoreAsync, setWorkCategoriesSyncStoreAsync } from '../store/workCategorySyncSlice';
import { useLocation } from './LocationProvider';
import { IMediaSettings, IMediaSettingsSyncStatus } from '../models/dms/systemConfiguration.model';
import { fetchMediaSettingsStoreAsync, setMediaSettingsHasSyncStoreAsync, setMediaSettingsSyncStoreAsync } from '../store/mediaSettingsSyncSlice';
import { fetchFranchisesStoreAsync, setFranchisesHasSyncStoreAsync, setFranchisesSyncStoreAsync } from '../store/franchiseSyncSlice';
import { IFranchiseSyncStatus } from '../models/dms/franchise.model';

interface IComponentProps {
  children: ReactNode;
}

let requestQueueProcessing = false;

const DataSyncProvider: React.FC<IComponentProps> = function ({ children }) {
  const [presentAlert] = useIonAlert();
  const dispatch = useAppDispatch();

  const [repairOrderSyncTimer, setRepairOrderSyncTimer] = useState<ITimerItem>();
  const [clockingDetailsSyncTimer, setClockingDetailsSyncTimer] = useState<ITimerItem>();
  const [delayCodesSyncTimer, setDelayCodesSyncTimer] = useState<ITimerItem>();
  const [workCategoriesSyncTimer, setWorkCategoriesSyncTimer] = useState<ITimerItem>();
  const [branchesSyncTimer, setBranchesSyncTimer] = useState<ITimerItem>();
  const [franchisesSyncTimer, setFranchisesSyncTimer] = useState<ITimerItem>();
  const [mediaSettingsSyncTimer, setMediaSettingsSyncTimer] = useState<ITimerItem>();
  const [requestQueueSyncTimer, setRequestQueueSyncTimer] = useState<ITimerItem>();

  const {
    actions: { stopDistanceTracking },
  } = useLocation();

  useEffect(() => {
    // Start timers
    // Clocking details
    let clockingDetailsSub: Subscription | undefined;
    setClockingDetailsSyncTimer((prev) => {
      const timer = startTimer(20000, true);
      logTransaction(LogType.Info, `Subscribing clockingDetailsSub`);
      clockingDetailsSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          fetchTechClockingDetails();
        }
      });

      return timer;
    });
    // Repair orders
    let repairOrderSub: Subscription | undefined;
    logTransaction(LogType.Info, `Starting repairOrderSyncTimer`);
    setRepairOrderSyncTimer((prev) => {
      const timer = startTimer(5000, true);

      logTransaction(LogType.Info, `Subscribing repairOrderSub`);
      repairOrderSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          syncRepairOrders();
        }
      });

      return timer;
    });
    // Delay codes
    let delayCodesSub: Subscription | undefined;
    logTransaction(LogType.Info, `Starting delayCodesSyncTimer`);
    setDelayCodesSyncTimer((prev) => {
      const timer = startTimer(5000, true);

      logTransaction(LogType.Info, `Subscribing delayCodesSub`);
      delayCodesSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          syncDelayCodes();
        }
      });

      return timer;
    });
    // Work categories
    let workCategoriesSub: Subscription | undefined;
    logTransaction(LogType.Info, `Starting workCategoriesSyncTimer`);
    setWorkCategoriesSyncTimer((prev) => {
      const timer = startTimer(5000, true);

      logTransaction(LogType.Info, `Subscribing workCategoriesSub`);
      workCategoriesSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          syncWorkCategories();
        }
      });

      return timer;
    });
    // Branches
    let branchesSub: Subscription | undefined;
    logTransaction(LogType.Info, `Starting branchesSyncTimer`);
    setBranchesSyncTimer((prev) => {
      const timer = startTimer(5000, true);

      logTransaction(LogType.Info, `Subscribing branchesSub`);
      branchesSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          syncBranches();
        }
      });

      return timer;
    });
    // Franchises
    let franchisesSub: Subscription | undefined;
    logTransaction(LogType.Info, `Starting franchisesSyncTimer`);
    setFranchisesSyncTimer((prev) => {
      const timer = startTimer(5000, true);

      logTransaction(LogType.Info, `Subscribing franchisesSub`);
      franchisesSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          syncFranchises();
        }
      });

      return timer;
    });
    // Media settings
    let mediaSettingsSub: Subscription | undefined;
    logTransaction(LogType.Info, `Starting mediaSettingsSyncTimer`);
    setMediaSettingsSyncTimer((prev) => {
      const timer = startTimer(5000, true);

      logTransaction(LogType.Info, `Subscribing mediaSettingsSub`);
      mediaSettingsSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          syncMediaSettings();
        }
      });

      return timer;
    });
    // Request queue
    let requestQueueSub: Subscription | undefined;
    logTransaction(LogType.Info, `Starting requestQueueSyncTimer`);
    setRequestQueueSyncTimer((prev) => {
      const timer = startTimer(10000, true);

      logTransaction(LogType.Info, `Subscribing requestQueueSub`);
      requestQueueSub = timer.subject.subscribe((id) => {
        if (id > 0) {
          processRequestQueue();
        }
      });

      return timer;
    });

    return () => {
      logTransaction(LogType.Info, `Stopping repairOrderSyncTimer`);
      repairOrderSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing repairOrderSub`);
      repairOrderSub?.unsubscribe();
      logTransaction(LogType.Info, `Stopping clockingDetailsSyncTimer`);
      clockingDetailsSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing clockingDetailsSub`);
      clockingDetailsSub?.unsubscribe();
      logTransaction(LogType.Info, `Stopping delayCodesSyncTimer`);
      delayCodesSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing delayCodesSub`);
      delayCodesSub?.unsubscribe();
      logTransaction(LogType.Info, `Stopping workCategoriesSyncTimer`);
      workCategoriesSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing workCategoriesSub`);
      workCategoriesSub?.unsubscribe();
      logTransaction(LogType.Info, `Stopping branchesSyncTimer`);
      branchesSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing branchesSub`);
      branchesSub?.unsubscribe();
      logTransaction(LogType.Info, `Stopping franchisesSyncTimer`);
      franchisesSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing franchisesSub`);
      franchisesSub?.unsubscribe();
      logTransaction(LogType.Info, `Stopping mediaSettingsSyncTimer`);
      mediaSettingsSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing mediaSettingsSub`);
      mediaSettingsSub?.unsubscribe();
      logTransaction(LogType.Info, `Stopping requestQueueSyncTimer`);
      requestQueueSyncTimer?.stop();
      logTransaction(LogType.Info, `Unsubscribing requestQueueSub`);
      requestQueueSub?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncRepairOrders = async () => {
    // Default interval=30*60*1000 (30 minutes)
    const interval = 30 * 60 * 1000;
    const currentTime = new Date().getTime();
    const { enableOfflineMode } = store.getState().settings;
    const { isAuthenticated } = store.getState().auth;
    const { repairOrderSync } = store.getState().repairOrderSync;

    if (!enableOfflineMode) {
      logTransaction(LogType.Warn, 'syncRepairOrders cancelled. enableOfflineMode = false');
    }

    if (isAuthenticated && enableOfflineMode) {
      const syncState =
        repairOrderSync ||
        ({
          hasSync: false,
          timestamp: 0,
          isProcessing: false,
          data: [],
        } as IRepairOrderSyncStatus);

      if (!syncState.isProcessing) {
        if (currentTime >= syncState.timestamp + interval || !syncState.hasSync) {
          await dispatch(
            setRepairOrderSyncStoreAsync({
              hasSync: false,
              timestamp: syncState.timestamp,
              isProcessing: true,
              data: syncState.data,
            } as IRepairOrderSyncStatus),
          );

          try {
            const resultAction = await dispatch(fetchRepairOrdersStoreAsync());
            unwrapResult(resultAction);
          } catch (ex) {
            // Log errors
            logTransaction(LogType.Error, `Caught error dispatching fetchRepairOrdersStoreAsync - error: ${(ex as IThunkError).message}`);
            await store.dispatch(setRepairOrderHasSyncStoreAsync(false));
          }
        } else {
          //logTransaction(LogType.Warn, `Repair order sync cancelled. currentTime ${currentTime} < timestamp ${syncState.timestamp + interval}`);
        }
      } else {
        logConsole(LogType.Warn, 'Repair order sync cancelled. syncState is currently processing');
      }
    }
  };

  const syncDelayCodes = async () => {
    // Default interval=24*60*60*1000 (24 hours)
    const interval = 24 * 60 * 60 * 1000;
    const currentTime = new Date().getTime();
    const { enableOfflineMode } = store.getState().settings;
    const { isAuthenticated } = store.getState().auth;
    const { delayCodesSync } = store.getState().delayCodeSync;

    if (!enableOfflineMode) {
      logTransaction(LogType.Warn, 'syncDelayCodes cancelled. enableOfflineMode = false');
    }

    if (isAuthenticated && enableOfflineMode) {
      const syncState =
        delayCodesSync ||
        ({
          hasSync: false,
          timestamp: 0,
          isProcessing: false,
          data: EmptyOdataResult<ITypeCode>(),
        } as IDelayCodeSyncStatus);

      if (!syncState.isProcessing) {
        if (currentTime >= syncState.timestamp + interval || !syncState.hasSync) {
          await dispatch(
            setDelayCodesSyncStoreAsync({
              hasSync: false,
              timestamp: syncState.timestamp,
              isProcessing: true,
              data: syncState.data,
            } as IDelayCodeSyncStatus),
          );

          try {
            const resultAction = await dispatch(fetchDelayCodesStoreAsync());
            unwrapResult(resultAction);
          } catch (ex) {
            // Log errors
            logTransaction(LogType.Error, `Caught error dispatching fetchDelayCodesStoreAsync - error: ${(ex as IThunkError).message}`);
            await store.dispatch(setDelayCodesHasSyncStoreAsync(false));
          }
        } else {
          //logTransaction(LogType.Warn, `Delay codes sync cancelled. currentTime ${currentTime} < timestamp ${syncState.timestamp + interval}`);
        }
      } else {
        logConsole(LogType.Warn, 'Delay codes sync cancelled. syncState is currently processing');
      }
    }
  };

  const syncWorkCategories = async () => {
    // Default interval=24*60*60*1000 (24 hours)
    const interval = 24 * 60 * 60 * 1000;
    const currentTime = new Date().getTime();
    const { enableOfflineMode } = store.getState().settings;
    const { isAuthenticated, technician } = store.getState().auth;
    const { workCategoriesSync } = store.getState().workCategorySync;

    if (!enableOfflineMode) {
      logTransaction(LogType.Warn, 'syncWorkCategories cancelled. enableOfflineMode = false');
    }

    if (isAuthenticated && enableOfflineMode && technician?.code) {
      const syncState =
        workCategoriesSync ||
        ({
          hasSync: false,
          timestamp: 0,
          isProcessing: false,
          data: EmptyOdataResult<IWorkCategory>(),
        } as IWorkCategorySyncStatus);

      if (!syncState.isProcessing) {
        if (currentTime >= syncState.timestamp + interval || !syncState.hasSync) {
          await dispatch(
            setWorkCategoriesSyncStoreAsync({
              hasSync: false,
              timestamp: syncState.timestamp,
              isProcessing: true,
              data: syncState.data,
            } as IWorkCategorySyncStatus),
          );

          try {
            const resultAction = await dispatch(fetchWorkCategoriesStoreAsync());
            unwrapResult(resultAction);
          } catch (ex) {
            // Log errors
            logTransaction(LogType.Error, `Caught error dispatching fetchWorkCategoriesStoreAsync - error: ${(ex as IThunkError).message}`);
            await store.dispatch(setWorkCategoriesHasSyncStoreAsync(false));
          }
        } else {
          //logTransaction(LogType.Warn, `Work categories sync cancelled. currentTime ${currentTime} < timestamp ${syncState.timestamp + interval}`);
        }
      } else {
        logConsole(LogType.Warn, 'Work categories sync cancelled. syncState is currently processing');
      }
    }
  };

  const syncBranches = async () => {
    // Default interval=24*60*60*1000 (24 hours)
    const interval = 24 * 60 * 60 * 1000;
    const currentTime = new Date().getTime();
    const { enableOfflineMode } = store.getState().settings;
    const { isAuthenticated } = store.getState().auth;
    const { branchesSync } = store.getState().branchSync;

    if (!enableOfflineMode) {
      logTransaction(LogType.Warn, 'syncBranches cancelled. enableOfflineMode = false');
    }

    if (isAuthenticated && enableOfflineMode) {
      const syncState =
        branchesSync ||
        ({
          hasSync: false,
          timestamp: 0,
          isProcessing: false,
          data: EmptyOdataResult<IBranch>(),
        } as IBranchSyncStatus);

      if (!syncState.isProcessing) {
        if (currentTime >= syncState.timestamp + interval || !syncState.hasSync) {
          await dispatch(
            setBranchesSyncStoreAsync({
              hasSync: false,
              timestamp: syncState.timestamp,
              isProcessing: true,
              data: syncState.data,
            } as IBranchSyncStatus),
          );

          try {
            const resultAction = await dispatch(fetchBranchesStoreAsync());
            unwrapResult(resultAction);
          } catch (ex) {
            // Log errors
            logTransaction(LogType.Error, `Caught error dispatching fetchBranchesStoreAsync - error: ${(ex as IThunkError).message}`);
            await store.dispatch(setBranchesHasSyncStoreAsync(false));
          }
        } else {
          //logTransaction(LogType.Warn, `Branches sync cancelled. currentTime ${currentTime} < timestamp ${syncState.timestamp + interval}`);
        }
      } else {
        logConsole(LogType.Warn, 'Branches sync cancelled. syncState is currently processing');
      }
    }
  };

  const syncFranchises = async () => {
    // Default interval=24*60*60*1000 (24 hours)
    const interval = 24 * 60 * 60 * 1000;
    const currentTime = new Date().getTime();
    const { enableOfflineMode } = store.getState().settings;
    const { isAuthenticated } = store.getState().auth;
    const { franchisesSync } = store.getState().franchiseSync;

    if (!enableOfflineMode) {
      logTransaction(LogType.Warn, 'syncFranchises cancelled. enableOfflineMode = false');
    }

    if (isAuthenticated && enableOfflineMode) {
      const syncState =
        franchisesSync ||
        ({
          hasSync: false,
          timestamp: 0,
          isProcessing: false,
          data: EmptyOdataResult<ITypeCode>(),
        } as IFranchiseSyncStatus);

      if (!syncState.isProcessing) {
        if (currentTime >= syncState.timestamp + interval || !syncState.hasSync) {
          await dispatch(
            setFranchisesSyncStoreAsync({
              hasSync: false,
              timestamp: syncState.timestamp,
              isProcessing: true,
              data: syncState.data,
            } as IFranchiseSyncStatus),
          );

          try {
            const resultAction = await dispatch(fetchFranchisesStoreAsync());
            unwrapResult(resultAction);
          } catch (ex) {
            // Log errors
            logTransaction(LogType.Error, `Caught error dispatching fetchFranchisesStoreAsync - error: ${(ex as IThunkError).message}`);
            await store.dispatch(setFranchisesHasSyncStoreAsync(false));
          }
        } else {
          //logTransaction(LogType.Warn, `Franchises sync cancelled. currentTime ${currentTime} < timestamp ${syncState.timestamp + interval}`);
        }
      } else {
        logConsole(LogType.Warn, 'Franchises sync cancelled. syncState is currently processing');
      }
    }
  };

  const syncMediaSettings = async () => {
    // Default interval=24*60*60*1000 (24 hours)
    const interval = 24 * 60 * 60 * 1000;
    const currentTime = new Date().getTime();
    const { enableOfflineMode } = store.getState().settings;
    const { isAuthenticated, technician } = store.getState().auth;
    const { mediaSettingsSync } = store.getState().mediaSettingsSync;

    if (!enableOfflineMode) {
      logTransaction(LogType.Warn, 'syncMediaSettings cancelled. enableOfflineMode = false');
    }

    if (isAuthenticated && enableOfflineMode && technician?.code) {
      const syncState =
        mediaSettingsSync ||
        ({
          hasSync: false,
          timestamp: 0,
          isProcessing: false,
          data: {} as IMediaSettings,
        } as IMediaSettingsSyncStatus);

      if (!syncState.isProcessing) {
        if (currentTime >= syncState.timestamp + interval || !syncState.hasSync) {
          await dispatch(
            setMediaSettingsSyncStoreAsync({
              hasSync: false,
              timestamp: syncState.timestamp,
              isProcessing: true,
              data: syncState.data,
            } as IMediaSettingsSyncStatus),
          );

          try {
            const resultAction = await dispatch(fetchMediaSettingsStoreAsync());
            unwrapResult(resultAction);
          } catch (ex) {
            // Log errors
            logTransaction(LogType.Error, `Caught error dispatching fetchMediaSettingsStoreAsync - error: ${(ex as IThunkError).message}`);
            await store.dispatch(setMediaSettingsHasSyncStoreAsync(false));
          }
        } else {
          //logTransaction(LogType.Warn, `Media settings sync cancelled. currentTime ${currentTime} < timestamp ${syncState.timestamp + interval}`);
        }
      } else {
        logConsole(LogType.Warn, 'Media settings sync cancelled. syncState is currently processing');
      }
    }
  };

  const fetchTechClockingDetails = async () => {
    const { isAuthenticated, technician } = store.getState().auth;
    const { clockingQueue, techCommentQueue, vehicleAttachmentQueue } = store.getState().requestQueue;
    const { isInAction, allowFetchClockings, allowOverrideAttendance, allowOverrideLabour, clockingFetchCooldown: delayInterval } = store.getState().app;
    const { attendanceClocking, labourClocking } = store.getState().technicianClocking;

    // Fetch the current clocking details an update the local state. Only sync once all requests
    // in the queue are cleared.

    if (delayInterval > 0) {
      dispatch(subtractClockingFetchCooldownState());
    }

    if (
      isAuthenticated &&
      allowFetchClockings &&
      technician?.code &&
      (clockingQueue?.length || 0) + (techCommentQueue?.length || 0) + (vehicleAttachmentQueue?.length || 0) === 0 &&
      delayInterval <= 0 &&
      (allowOverrideAttendance || allowOverrideLabour) &&
      !isInAction
    ) {
      dispatch(setAllowFetchClockingsState(false));

      // Stores a positive value if any change was detected to labour that requires user input. This is so attendance is ignored until the next loop.
      let labourChange = false;

      // Fetch tech clockings

      // There is a special case here where if the labour clocking is on and was set on manually, then there is no need
      // to fetch data from the server since there is no logic state below that will require change. This is because there
      // is no way the attendance can change (since we're clocked on labour) and no way the labour can change because
      // we clocked on manually.

      // Get data from server
      try {
        const serverClockingResultAction = await dispatch(fetchClockingDetailsAsync(technician.code));
        const serverClocking = unwrapResult(serverClockingResultAction) as ITechClockingDetail;

        if (serverClocking) {
          // Start with labour clockings

          // Labour always matches its attendance. Eg. you can't have labour without attendance. Saying that, if you have manually
          // clocked in attendance then you can't sync with any exising labour from the server because it would have a different
          // attendance pair. So accepting any labour from the server will also override the local attendance.

          if (serverClocking.labourClocking?.endDateTime === undefined) {
            // Server labour clocking is ON
            // Only ask to clock sync to ON if the current labour clocking is OFF
            // OR if the current labour clocking is ON and the ON state came from a previously synced state
            if (labourClocking?.direction === undefined || labourClocking?.direction === ClockingDirection.Off || (labourClocking?.direction === ClockingDirection.On && labourClocking.fromServer)) {
              // Local labour clocking is OFF OR clocking is ON and the ON state came from a previously synced state
              // Only ask if the user hasn't denied this previously
              if (allowOverrideLabour) {
                // Before notifying user, the server labour may be ON because the endpoint is still saving the record to the database
                // Check to make sure the last labour sync does not match the local labour
                if (!techClockingMatchesLabourClocking(serverClocking, labourClocking || ({} as ILabourClocking))) {
                  labourChange = true;
                  showClockSyncOnLabourConfirm(serverClocking);
                } else {
                  logTransaction(LogType.Info, `Not syncing labour ON, tech clocking matches`);
                }
              } else {
                logTransaction(LogType.Info, `Not syncing labour ON, allowOverrideLabour = false`);
              }
            } else {
              // Local labour clocking is ON
              // Ignore this logic state
              logTransaction(LogType.Info, `Not syncing labour ON, local clocking is BREAK or ON and is not from server`);
            }
          } else {
            // Server labour clocking is OFF
            // Never ask to sync to OFF, do this automatically, however only do this if the current labour clocking is ON and the
            // ON state came from a previously synced state OR if the labour clockings match
            if (labourClocking?.direction === ClockingDirection.On) {
              // Local labour clocking is ON
              // Only sync to OFF if the current ON state came from a previously synced state or if the labour clockings match
              if (labourClocking.fromServer || techClockingMatchesLabourClocking(serverClocking, labourClocking)) {
                labourChange = true;
                setLabourClockingStateOff(serverClocking);
              } else {
                logTransaction(LogType.Info, `Not syncing labour OFF, clocking is ON and is not from server`);
              }
            } else {
              // Local labour clocking is OFF
              // Ignore this logic state
              logTransaction(LogType.Info, `Not syncing labour OFF, clocking is not ON`);
            }
          }

          // Then do attendance clockings if no labour changes occurred

          if (!labourChange) {
            // Its similar logic to the labour
            if (serverClocking.attendanceClocking?.endDateTime === undefined) {
              // Server attendance clocking is ON
              // Only ask to clock sync to ON if the current attendance clocking is off and allowOverrideAttendance
              if (attendanceClocking?.direction === undefined || attendanceClocking?.direction === ClockingDirection.Off) {
                // Local attendance clocking is OFF
                // Only ask if the user hasn't denied this previously
                if (allowOverrideAttendance) {
                  // Before notifying user, the server attendance may be ON because the endpoint is still saving the record to the database
                  // Check to make sure the last attendance sync does not match the local attendance
                  if (!techClockingMatchesAttendanceClocking(serverClocking, attendanceClocking || ({} as IAttendanceClocking))) {
                    showClockSyncOnAttendanceConfirm(serverClocking);
                  } else {
                    logTransaction(LogType.Info, `Not syncing attendance ON, tech clocking matches`);
                  }
                } else {
                  logTransaction(LogType.Info, `Not syncing attendance ON, allowOverrideAttendance = false`);
                }
              } else {
                // Local attendance clocking is ON
                // Ignore this logic state
                logTransaction(LogType.Info, `Not syncing attendance ON, local clocking is ON or BREAK`);
              }
            } else {
              // Server attendance clocking is OFF
              // Never ask to sync to OFF, do this automatically, however only do this if the current attendance clocking is ON and the
              // ON state came from a previously synced state
              if (attendanceClocking?.direction === ClockingDirection.On) {
                // Local attendance clocking is ON
                // Ensure labour clocking is OFF
                if (labourClocking?.direction === ClockingDirection.Off) {
                  // Only sync to OFF if the current ON state came from a previously synced state or if the attendance clockings match
                  if (attendanceClocking.fromServer || techClockingMatchesAttendanceClocking(serverClocking, attendanceClocking)) {
                    setAttendanceClockingStateOff(serverClocking);
                  } else {
                    logTransaction(LogType.Info, `Not syncing attendance OFF, clocking doesn't match or not from server`);
                  }
                } else {
                  // Local labour clocking is ON
                  // Ignore this logic state
                  logTransaction(LogType.Info, `Not syncing attendance OFF, labour clocking is ON`);
                }
              } else {
                // Local attendance clocking is OFF
                // Ignore this logic state
                logTransaction(LogType.Info, `Not syncing attendance OFF, clocking is not ON`);
              }
            }
          }
        }
      } catch (err: any) {
        logTransaction(LogType.Error, `Caught error dispatching FetchTechClockingDetails - error: ${err?.message}`);
      } finally {
        dispatch(setAllowFetchClockingsState(true));
      }
    } else {
      logConsole(LogType.Warn, 'fetchTechClockingDetails cancelled');
      logTransaction(
        LogType.Warn,
        `${technician?.code}|${isAuthenticated ? 'AUTH ' : 'NAUTH'}|${allowFetchClockings ? 'FETCH ' : 'NFETCH'}|QUE=${(clockingQueue?.length || 0) + (techCommentQueue?.length || 0) + (vehicleAttachmentQueue?.length || 0)}|DEL=${delayInterval}|${
          allowOverrideAttendance ? 'OVERRA ' : 'NOVERRA'
        }|${allowOverrideLabour ? 'OVERRL ' : 'NOVERRL'}|${isInAction ? 'IIA ' : 'NIIA'}`,
      );
    }
  };

  const techClockingMatchesAttendanceClocking = (clockings: ITechClockingDetail, attendanceClocking: IAttendanceClocking) => {
    // Note: seconds on the server are rounded down
    const serverStartDate = getStateStringAsMoment(clockings.attendanceClocking.startDateTime).format('YYYY-MM-DDTHH:mm:00');
    const localStartDate = getStateStringAsMoment(attendanceClocking.startDateTime).format('YYYY-MM-DDTHH:mm:00');
    if (serverStartDate === localStartDate) {
      return true;
    }
    return false;
  };

  const techClockingMatchesLabourClocking = (clockings: ITechClockingDetail, labourClocking: ILabourClocking) => {
    // Note: seconds on the server are rounded down
    const serverStartDate = getStateStringAsMoment(clockings.labourClocking.startDateTime).format('YYYY-MM-DDTHH:mm:00');
    const localStartDate = getStateStringAsMoment(labourClocking.startDateTime).format('YYYY-MM-DDTHH:mm:00');
    if (serverStartDate === localStartDate && clockings.labourClocking.repairOrderNumber === labourClocking.jobData?.repairOrderNumber && clockings.labourClocking.jobCode === labourClocking.jobData.jobCode) {
      return true;
    }
    return false;
  };

  const setLabourClockingStateOff = async (clocking: ITechClockingDetail) => {
    dispatch(setAllowOverrideLabourState(true));
    await dispatch(
      setLabourClockingStoreAsync({
        direction: ClockingDirection.Off,
        type: ClockingType.Labour,
        technicianCode: clocking.technicianCode,
        startDateTime: clocking.labourClocking.startDateTime,
        endDateTime: clocking.labourClocking.endDateTime,
        fromServer: true,
      } as ILabourClocking),
    );
    // Alert the user to this change
    showClockSyncOffLabourConfirm();
    // Cancel any distance tracking
    await stopDistanceTracking();
  };

  const setLabourClockingStateOn = async (clockings: ITechClockingDetail) => {
    dispatch(setAllowOverrideLabourState(true));
    await dispatch(
      setLabourClockingStoreAsync({
        direction: ClockingDirection.On,
        type: ClockingType.Labour,
        technicianCode: clockings.technicianCode,
        startDateTime: clockings.labourClocking.startDateTime,
        endDateTime: undefined,
        fromServer: true,
        jobData: {
          branchCode: clockings.labourClocking.branchCode,
          repairOrderNumber: clockings.labourClocking.repairOrderNumber,
          jobCode: clockings.labourClocking.jobCode,
          jobType: clockings.labourClocking.jobType,
        },
        details: {
          distanceRaw: 0,
        },
        position: {},
        repairOrder: {
          branch: clockings.labourClocking.branchCode,
          repairOrderNumber: clockings.labourClocking.repairOrderNumber,
          vehicle: {
            source: clockings.labourClocking.source,
            sourceRef: clockings.labourClocking.sourceRef,
          },
        },
      } as ILabourClocking),
    );

    syncLabourToCache(clockings.labourClocking.repairOrderNumber, clockings.labourClocking);

    // Clock in attendance as well. This must be done to match the labour clocking.
    setAttendanceClockingStateOn(clockings);

    // Cancel any distance tracking
    await stopDistanceTracking();
  };

  const setAttendanceClockingStateOn = async (clockings: ITechClockingDetail) => {
    dispatch(setAllowOverrideAttendanceState(true));
    await dispatch(
      setAttendanceClockingStoreAsync({
        direction: ClockingDirection.On,
        type: ClockingType.Attendance,
        technicianCode: clockings.technicianCode,
        startDateTime: clockings.attendanceClocking.startDateTime,
        endDateTime: undefined,
        fromServer: true,
      } as IAttendanceClocking),
    );
  };

  const setAttendanceClockingStateOff = async (clockings: ITechClockingDetail) => {
    dispatch(setAllowOverrideAttendanceState(true));
    await dispatch(
      setAttendanceClockingStoreAsync({
        direction: ClockingDirection.Off,
        type: ClockingType.Attendance,
        technicianCode: clockings.technicianCode,
        startDateTime: clockings.attendanceClocking.startDateTime,
        endDateTime: clockings.attendanceClocking.endDateTime,
        fromServer: true,
      } as IAttendanceClocking),
    );
    // Alert the user to this change
    showClockSyncOffAttendanceConfirm();
  };

  const syncLabourToCache = async (repairOrderNumber: number, labourClocking: ITechClockingLabour) => {
    // When synchronising labour from the server, also synchronise the job data as well. This will ensure that clocked labour that is not
    // in the cache will be available if the tech goes offline again
    const cachedRo = store.getState().repairOrderSync.repairOrderSync?.data.find((ro) => ro.repairOrderNumber === repairOrderNumber);
    if (!cachedRo) {
      logTransaction(LogType.Warn, `After syncing labour, the RO ${repairOrderNumber} does not exist in the repair order cache`);
      // RO is not cached. Try to load details from the server but if that still fails, load the bare minimum
      try {
        const roSearch = await getRepairOrderByRepairOrderNumber(repairOrderNumber);
        const roWithJobs = mapRepairOrderDetailToRepairOrderWithJobs(roSearch);
        if (roWithJobs) {
          await dispatch(setSelectedRepairOrderStoreAsync(roWithJobs));
        } else {
          throw new Error(`Could not find RO in database matching RO number ${repairOrderNumber}`);
        }
      } catch (ex: any) {
        logTransaction(LogType.Warn, `Failed to fetch repair order ${repairOrderNumber}. ${ex.message}. Adding basic RO and job details from information found in sync data`);
        // Either failed to load from the database or the RO was not found given the RO number
        const roWithJobs = {
          fromDetail: false,
          branch: labourClocking.branchCode,
          repairOrderNumber: labourClocking.repairOrderNumber,
          jobs: [
            {
              branchCode: labourClocking.branchCode,
              jobCode: labourClocking.jobCode,
              jobType: labourClocking.jobType,
              repairOrderNumber: labourClocking.repairOrderNumber,
            } as IRepairOrderJob,
          ],
        } as IRepairOrder;
        await dispatch(setSelectedRepairOrderStoreAsync(roWithJobs));
      }
    }
  };

  const showClockSyncOnAttendanceConfirm = async (clockings: ITechClockingDetail) => {
    logTransaction(LogType.Info, `Alert tech clocked in attendance elsewhere`);
    try {
      await presentAlert({
        header: 'Sync Attendance?',
        subHeader: '',
        message: 'You are clocked in attendance on another device. Would you like to sync this clocking to your current device?',
        buttons: [
          {
            text: 'No',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: () => {
              logTransaction(LogType.Info, `Tech selected NO`);
              dispatch(setAllowOverrideAttendanceState(false));
            },
          },
          {
            text: 'Yes',
            cssClass: 'secondary-button',
            handler: () => {
              logTransaction(LogType.Info, `Tech selected YES`);
              setAttendanceClockingStateOn(clockings);
            },
          },
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
          dispatch(setAllowFetchClockingsState(true));
        },
      });
    } finally {
      dispatch(setIsInActionState(true));
    }
  };

  const showClockSyncOffAttendanceConfirm = async () => {
    logTransaction(LogType.Info, `Alert tech clocked off attendance elsewhere forced`);
    try {
      await presentAlert({
        header: 'Attendance Synced',
        subHeader: '',
        message: 'You clocked out attendance on another device. This clocking has been automatically synced to your current device.',
        buttons: [
          {
            text: 'OK',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: () => {
              dispatch(setAllowOverrideAttendanceState(true));
            },
          },
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
          dispatch(setAllowFetchClockingsState(true));
        },
      });
    } finally {
      dispatch(setIsInActionState(true));
    }
  };

  const showClockSyncOnLabourConfirm = async (clockings: ITechClockingDetail) => {
    logTransaction(LogType.Info, `Alert tech clocked in labour elsewhere`);
    try {
      await presentAlert({
        header: 'Sync Labour?',
        subHeader: '',
        message: 'You are clocked on labour on another device. Would you like to sync this clocking to your current device?',
        buttons: [
          {
            text: 'No',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: () => {
              logTransaction(LogType.Info, `Tech selected NO`);
              dispatch(setAllowOverrideLabourState(false));
            },
          },
          {
            text: 'Yes',
            cssClass: 'secondary-button',
            handler: () => {
              logTransaction(LogType.Info, `Tech selected YES`);
              setLabourClockingStateOn(clockings);
            },
          },
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
          dispatch(setAllowFetchClockingsState(true));
        },
      });
    } finally {
      dispatch(setIsInActionState(true));
    }
  };

  const showClockSyncOffLabourConfirm = async () => {
    logTransaction(LogType.Info, `Alert tech clocked off labour elsewhere forced`);
    try {
      await presentAlert({
        header: 'Labour Synced',
        subHeader: '',
        message: 'You clocked off labour on another device. This clocking has been automatically synced to your current device.',
        buttons: [
          {
            text: 'OK',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: () => {
              logTransaction(LogType.Info, `Tech automatically said YES`);
              dispatch(setAllowOverrideLabourState(true));
            },
          },
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
          dispatch(setAllowFetchClockingsState(true));
        },
      });
    } finally {
      dispatch(setIsInActionState(true));
    }
  };

  const processRequestQueue = async () => {
    try {
      const { isAuthenticated, isRefreshingToken } = store.getState().auth;

      const requestQueue = getRequestQueueItems();

      if (isAuthenticated && !requestQueueProcessing) {
        logConsole(LogType.Info, `Processing request queue`);

        if (requestQueue?.length) {
          logTransaction(LogType.Info, `Processing request queue. requestQueueLength=${requestQueue?.length} | requestProcessing=${requestQueueProcessing ? 'true' : 'false'} | refreshTokenInProgress=${isRefreshingToken ? 'true' : 'false'}`);
        }

        if (requestQueue?.length && !requestQueueProcessing && !isRefreshingToken) {
          requestQueueProcessing = true;

          // Currently only handling single document type
          const requestItem = requestQueue.shift();

          // Process request type
          switch (requestItem?.type) {
            case RequestQueueType.TechAttendanceOn:
              const attendanceDataOn = requestItem.data as IAttendanceClocking;
              if (attendanceDataOn.startDateTime) {
                try {
                  const resultAction = await dispatch(postClockOnAttendanceAsync({ techCode: attendanceDataOn.technicianCode, startDateTime: attendanceDataOn.startDateTime, position: attendanceDataOn.position }));
                  unwrapResult(resultAction);
                  // Success
                  // Remove request item from queue
                  await dispatch(removeFromRequestQueueStoreAsync(requestItem));
                } catch (ex) {
                  // Log errors
                  const error = (ex as IThunkError).message;
                  logTransaction(LogType.Error, error);
                  dispatch(
                    updateRequestQueueStoreAsync({
                      ...requestItem,
                      error: `Attendance IN is failing to sync. ${error}`,
                    } as IClockingRequestQueueItem),
                  );
                } finally {
                  // Complete
                  requestQueueProcessing = false;
                }
              } else {
                logTransaction(LogType.Error, 'Data for TechAttendanceOn contains no startDateTime! Removing from request queue');
                await dispatch(removeFromRequestQueueStoreAsync(requestItem));
              }
              break;
            case RequestQueueType.TechAttendanceOff:
              const attendanceDataOff = requestItem.data as IAttendanceClocking;
              if (attendanceDataOff.endDateTime) {
                try {
                  const resultAction = await dispatch(postClockOffAttendanceAsync({ techCode: attendanceDataOff.technicianCode, endDateTime: attendanceDataOff.endDateTime, position: attendanceDataOff.position }));
                  unwrapResult(resultAction);
                  // Success
                  // Remove request item from queue
                  await dispatch(removeFromRequestQueueStoreAsync(requestItem));
                } catch (ex) {
                  // Log errors
                  const error = (ex as IThunkError).message;
                  logTransaction(LogType.Error, error);
                  dispatch(
                    updateRequestQueueStoreAsync({
                      ...requestItem,
                      error: `Attendance OUT is failing to sync. ${error}`,
                    } as IClockingRequestQueueItem),
                  );
                } finally {
                  // Complete
                  requestQueueProcessing = false;
                }
              } else {
                logTransaction(LogType.Error, 'Data for TechAttendanceOff contains no endDateTime! Removing from request queue');
                await dispatch(removeFromRequestQueueStoreAsync(requestItem));
              }
              break;
            case RequestQueueType.TechLabourOn:
              const labourDataOn = requestItem.data as ILabourClocking;
              if (labourDataOn.startDateTime) {
                try {
                  const resultAction = await dispatch(
                    postClockOnLabourAsync({ techCode: labourDataOn.technicianCode, startDateTime: labourDataOn.startDateTime, jobData: labourDataOn.jobData, repairOrder: labourDataOn.repairOrder, position: labourDataOn.position }),
                  );
                  unwrapResult(resultAction);
                  // Success
                  // Remove request item from queue
                  await dispatch(removeFromRequestQueueStoreAsync(requestItem));
                } catch (ex) {
                  // Log errors
                  const error = (ex as IThunkError).message;
                  logTransaction(LogType.Error, error);
                  dispatch(
                    updateRequestQueueStoreAsync({
                      ...requestItem,
                      error: `Labour ON is failing to sync. ${error}`,
                    } as IClockingRequestQueueItem),
                  );
                } finally {
                  // Complete
                  requestQueueProcessing = false;
                }
              } else {
                logTransaction(LogType.Error, 'Data for TechLabourOn contains no startDateTime! Removing from request queue');
                await dispatch(removeFromRequestQueueStoreAsync(requestItem));
              }
              break;
            case RequestQueueType.TechLabourOff:
              let labourDataOff = requestItem.data as ILabourClocking;
              let hasError = false;

              if (labourDataOff.endDateTime && labourDataOff.jobData) {
                try {
                  if (labourDataOff.details) {
                    if (labourDataOff.details.distanceRaw > 0 && !labourDataOff.details.distanceAccurate) {
                      const distance = labourDataOff.details.distanceRaw;
                      //const distance = await getAccurateDistance(labourDataOff.details.distanceLocationsRaw);
                      labourDataOff = {
                        ...labourDataOff,
                        details: {
                          ...labourDataOff.details,
                          distanceAccurate: distance,
                        },
                      };
                      await dispatch(
                        updateRequestQueueStoreAsync({
                          id: requestItem.id,
                          data: {
                            ...labourDataOff,
                          },
                        } as IClockingRequestQueueItem),
                      );
                    }
                  }
                } catch (ex) {
                  logTransaction(LogType.Error, `Error capturing accurate distance. ${(ex as any).message}`);
                  hasError = hasError || true;
                }

                if (!hasError) {
                  try {
                    const resultAction = await dispatch(
                      postClockOffLabourAsync({
                        techCode: labourDataOff.technicianCode,
                        endDateTime: labourDataOff.endDateTime!,
                        jobData: labourDataOff.jobData,
                        repairOrder: labourDataOff.repairOrder,
                        details: labourDataOff.details,
                        position: labourDataOff.position,
                      }),
                    );
                    unwrapResult(resultAction);
                    // Success
                    // Remove request item from queue
                    await dispatch(removeFromRequestQueueStoreAsync(requestItem));
                  } catch (ex) {
                    // Log errors
                    const error = (ex as IThunkError).message;
                    logTransaction(LogType.Error, error);
                    dispatch(
                      updateRequestQueueStoreAsync({
                        ...requestItem,
                        error: `Labour OFF is failing to sync. ${error}`,
                      } as IClockingRequestQueueItem),
                    );
                  } finally {
                    // Complete
                    requestQueueProcessing = false;
                  }
                }

                if (hasError) {
                  requestQueueProcessing = false;
                }
              } else {
                if (!labourDataOff.jobData) {
                  logTransaction(LogType.Error, 'Data for TechLabourOff contains no job data! Removing from request queue');
                }
                if (!labourDataOff.endDateTime) {
                  logTransaction(LogType.Error, 'Data for TechLabourOff contains no endDateTime! Removing from request queue');
                }
                await dispatch(removeFromRequestQueueStoreAsync(requestItem));
              }
              break;
            case RequestQueueType.TechComment:
              const techComment = requestItem.data as ITechComment;
              if (techComment.comment.trim()) {
                try {
                  const resultAction = await dispatch(postTechnicianCommentAsync(techComment));
                  unwrapResult(resultAction);
                  // Success
                  // Remove request item from queue
                  await dispatch(removeFromRequestQueueStoreAsync(requestItem));
                } catch (ex) {
                  // Log errors
                  const error = (ex as IThunkError).message;
                  logTransaction(LogType.Error, error);
                  dispatch(
                    updateRequestQueueStoreAsync({
                      ...requestItem,
                      error: `Tech comment is failing to sync. ${error}`,
                    } as ITechCommentRequestQueueItem),
                  );
                } finally {
                  // Complete
                  requestQueueProcessing = false;
                }
              } else {
                logTransaction(LogType.Error, 'Data for TechComment contains no comment! Removing from request queue');
                await dispatch(removeFromRequestQueueStoreAsync(requestItem));
              }
              break;
            case RequestQueueType.VehicleAttachment:
              const attachment = requestItem.data as IAttachmentContainer;
              try {
                let resultAction: any;
                let result: IUpdateMediaAttachments;

                dispatch(setAttachmentsProcessingState(true));

                switch (attachment.assetType) {
                  case AttachmentAssetType.WorkshopVehicle:
                  case AttachmentAssetType.StockUnit:
                  case AttachmentAssetType.FixedAsset:
                    resultAction = await dispatch(
                      postVehicleAttachmentAsync({
                        attachment,
                      }),
                    );
                    result = unwrapResult(resultAction) as IUpdateMediaAttachments;
                    // Success
                    // Remove request item from queue
                    await dispatch(removeFromRequestQueueStoreAsync(requestItem));
                    break;
                }

                // After posting attachments, append new attachements to other outstanding attachment sync requests with matching RO
                let outstandingAttachmentSyncRequests = store.getState().requestQueue.vehicleAttachmentQueue?.filter((a) => a.data.repairOrderNumber === result.refNo);
                outstandingAttachmentSyncRequests?.forEach(async (a) => {
                  const newAttachmentItem = {
                    ...a,
                    data: {
                      ...a.data,
                      attachments: [...result.attachments, ...a.data.attachments.filter((a) => a.action === AttachmentAction.Add)] as IAttachment[],
                    } as IAttachmentContainer,
                  } as IVehicleAttachmentRequestQueueItem;
                  await dispatch(updateRequestQueueStoreAsync(newAttachmentItem));
                });
              } catch (ex) {
                // Log errors
                const error = (ex as IThunkError).message;
                logTransaction(LogType.Error, error);
                dispatch(
                  updateRequestQueueStoreAsync({
                    ...requestItem,
                    error: `Asset image is failing to sync. ${error}`,
                  } as IVehicleAttachmentRequestQueueItem),
                );
              } finally {
                // Complete
                requestQueueProcessing = false;
                dispatch(setAttachmentsProcessingState(false));
              }
              break;
          }
        }
        //}
      }
    } catch (ex) {
      requestQueueProcessing = false;
    }
  };

  const getRequestQueueItems = () => {
    const { clockingQueue, techCommentQueue, vehicleAttachmentQueue } = store.getState().requestQueue;
    return [...(clockingQueue || ([] as IClockingRequestQueueItem[])), ...(techCommentQueue || ([] as ITechCommentRequestQueueItem[])), ...(vehicleAttachmentQueue || ([] as IVehicleAttachmentRequestQueueItem[]))];
  };

  return (
    <DataSyncContext.Provider
      value={{
        state: { repairOrderSyncTimer, clockingDetailsSyncTimer, delayCodesSyncTimer, workCategoriesSyncTimer, branchesSyncTimer, franchisesSyncTimer, mediaSettingsSyncTimer, requestQueueSyncTimer },
        actions: {
          processRequestQueue,
          getRequestQueueItems,
        },
      }}>
      {children}
    </DataSyncContext.Provider>
  );
};

const dataSyncProvider = {
  state: {} as {
    repairOrderSyncTimer: ITimerItem | undefined;
    clockingDetailsSyncTimer: ITimerItem | undefined;
    delayCodesSyncTimer: ITimerItem | undefined;
    workCategoriesSyncTimer: ITimerItem | undefined;
    branchesSyncTimer: ITimerItem | undefined;
    franchisesSyncTimer: ITimerItem | undefined;
    mediaSettingsSyncTimer: ITimerItem | undefined;
    requestQueueSyncTimer: ITimerItem | undefined;
  },
  actions: {} as {
    processRequestQueue: () => Promise<void>;
    getRequestQueueItems: () => (IClockingRequestQueueItem | ITechCommentRequestQueueItem | IVehicleAttachmentRequestQueueItem)[];
  },
};

export const DataSyncContext = createContext<typeof dataSyncProvider>(dataSyncProvider);
export const useDataSync = () => useContext(DataSyncContext);
export default DataSyncProvider;
